/* eslint-disable react/prop-types */
import React, { memo, useState, useEffect, useRef } from 'react';
import {
  Modal,
  View,
  Image,
  TouchableOpacity,
  Dimensions,
  Alert,
  Linking,
} from 'react-native';
import { QText } from './index';
import { useTheme } from 'react-native-paper';
import { makeStyles } from '../hooks/MakeStylesHook';
import CloseIcon from './Icons/CloseIcon';
import SecureFileOpener from './SecureFileOpener';
import { useGetSecureAttachmentQuery } from '../store/api/QueueApi';
import { getObjectNameFromUrl } from '../utils/secureFileAccess';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const useStyles = makeStyles((theme) => ({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    position: 'absolute',
    top: 50,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    zIndex: 2,
  },
  title: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    marginRight: 15,
  },
  closeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 20,
    padding: 8,
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  image: {
    width: 'auto',
    height: 'auto',
    maxWidth: screenWidth - 40,
    maxHeight: screenHeight - 140,
    minWidth: 100,
    minHeight: 100,
    borderRadius: 8,
  },
  errorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorText: {
    color: 'white',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '500',
  },
  footer: {
    position: 'absolute',
    bottom: 50,
    left: 20,
    right: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 12,
    padding: 15,
  },
  footerText: {
    color: 'white',
    fontSize: 14,
    textAlign: 'center',
    opacity: 0.9,
  },
}));

const FullScreenImageViewer = ({
  visible,
  onClose,
  imageFile,
  queueUserId,
}) => {
  const styles = useStyles();
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [secureFileRequest, setSecureFileRequest] = useState(null);
  const [secureImageUrl, setSecureImageUrl] = useState(null);
  const imageUrlRef = useRef(null);

  const imageUrl = imageFile?.uri;
  const imageName = imageFile?.name || 'Image';

  // Handle secure image URL creation
  useEffect(() => {
    const loadSecureImage = async () => {
      if (!visible || !imageFile || !queueUserId) {
        setSecureImageUrl(null);
        return;
      }

      try {
        setImageLoading(true);
        setImageError(false);

        const currentImageUrl = imageFile?.uri;

        // Prevent infinite loops by checking if URL has changed
        if (imageUrlRef.current === currentImageUrl && secureImageUrl) {
          setImageLoading(false);
          return;
        }

        imageUrlRef.current = currentImageUrl;

        // Use the utility function to extract object name or get direct URL
        const objectName = getObjectNameFromUrl(currentImageUrl);
        const secureUrl = objectName || currentImageUrl;
        if (secureUrl) {
          setSecureImageUrl(secureUrl);
        } else {
          console.error(
            'Could not create secure image URL for:',
            currentImageUrl
          );
          setImageError(true);
          setImageLoading(false);
        }
      } catch (error) {
        console.error('Error loading secure image:', error);
        setImageError(true);
        setImageLoading(false);
      }
    };

    loadSecureImage();

    // Cleanup blob URL when component unmounts or changes
    return () => {
      if (secureImageUrl && secureImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(secureImageUrl);
      }
    };
  }, [visible, imageFile, queueUserId]); // Removed imageUrl from dependencies

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleRetry = () => {
    setImageError(false);
    setImageLoading(true);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Size unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!visible || !imageFile) return null;

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent={true}
    >
      <View style={styles.overlay}>
        {/* Header */}
        <View style={styles.header}>
          <QText style={styles.title} numberOfLines={2}>
            {imageName}
          </QText>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <CloseIcon width={24} height={24} color="white" />
          </TouchableOpacity>
        </View>

        {/* Image Container */}
        <View style={styles.container}>
          {imageError ? (
            <View style={styles.errorContainer}>
              <QText style={styles.errorText}>
                Failed to load image. This might be due to network issues or
                server permissions.
              </QText>
              <TouchableOpacity
                style={styles.retryButton}
                onPress={handleRetry}
              >
                <QText style={styles.retryButtonText}>Retry</QText>
              </TouchableOpacity>
              {queueUserId && (
                <TouchableOpacity
                  style={[
                    styles.retryButton,
                    {
                      marginTop: 10,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  ]}
                  onPress={async () => {
                    try {
                      if (queueUserId) {
                        // Use the utility function to extract object name
                        const objectName = getObjectNameFromUrl(imageUrl);
                        if (objectName) {
                          setSecureFileRequest({ queueUserId, objectName });
                          return;
                        } else {
                          Alert.alert(
                            'Error',
                            'Unable to extract file information'
                          );
                          return;
                        }
                      }

                      // Fallback to direct URL if secure access fails
                      Linking.openURL(imageUrl).catch(() => {
                        Alert.alert('Error', 'Unable to open image URL');
                      });
                    } catch (error) {
                      Alert.alert(
                        'Error',
                        'Unable to open file: ' + error.message
                      );
                    }
                  }}
                >
                  <QText style={styles.retryButtonText}>Open in Browser</QText>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <View style={styles.imageContainer}>
              {secureImageUrl && (
                <Image
                  source={{
                    uri: secureImageUrl,
                    headers: {
                      Accept: 'image/*',
                    },
                  }}
                  style={styles.image}
                  resizeMode="contain"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              )}
              {imageLoading && (
                <View style={styles.errorContainer}>
                  <QText style={styles.errorText}>Loading image...</QText>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Footer with file info */}
        <View style={styles.footer}>
          <QText style={styles.footerText}>
            {imageFile?.size && imageFile.size > 0
              ? `${formatFileSize(imageFile.size)} • ${imageFile?.type || 'Image'}`
              : imageFile?.type || 'Image'}
          </QText>
        </View>
      </View>

      {/* Secure File Opener */}
      {secureFileRequest && (
        <SecureFileOpener
          queueUserId={secureFileRequest.queueUserId}
          objectName={secureFileRequest.objectName}
          onComplete={() => setSecureFileRequest(null)}
        />
      )}
    </Modal>
  );
};

export default memo(FullScreenImageViewer);
